import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';

export const LoginRequest = createAsyncThunk('auth/login', async (data, { rejectWithValue }) => {
  const url = process.env.REACT_APP_API_URL;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*'
    }
  };
  try {
    const res = await axios.post(`${url}/signin`, data, config);
    toast.success('Welcome to Emma Vendor Portal');
    return {
      ...res.data,
      username: data.username
    };
  } catch (e) {
    toast.error(e.response.data);
    return rejectWithValue(e.response.data);
  }
});

export const RefreshTokenRequest = createAsyncThunk(
  'auth/refresh',
  async (data, { rejectWithValue, getState }) => {
    const url = process.env.REACT_APP_API_URL;
    const { auth } = getState();
    const config = {
      headers: {
        Authorization: `Bearer ${auth.id_token}`,
        'Content-Type': 'application/json',
        Accept: '*/*'
      }
    };
    try {
      const res = await axios.post(`${url}/refresh`, { refresh_token: auth.refresh_token }, config);
      return {
        ...res.data
      };
    } catch (e) {
      toast.error(e.response.data);
      return rejectWithValue(e.response.data);
    }
  }
);

export const ConfirmationRequest = createAsyncThunk(
  'auth/verify',
  async (data, { rejectWithValue }) => {
    const url = process.env.REACT_APP_API_URL;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*'
      }
    };
    try {
      const res = await axios.post(`${url}/confirm-sign-up`, data, config);
      Swal.fire({
        title: "You're all set!",
        text: 'Your account has been successfully activated!',
        icon: 'success',
        confirmButtonText: 'Login Now'
      }).then((result) => {
        window.location.href = `${window.location.origin}/login`;
      });
      return {
        ...res
      };
    } catch (e) {
      toast.error(e.response.data);
      return rejectWithValue(e.response.data);
    }
  }
);

const initialState = {
  isAuthenticated: false,
  username: '',
  access_token: '',
  id_token: '',
  refresh_token: '',
  token_type: '',
  role: '',
  vendor: [],
  can_approve_po: false,
  isSocketConnected: false,
  isRefreshCalled: false
};

const AuthSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (dispatch) => {
      Cookies.remove('expires_in');
      Cookies.remove('currentVendor');
      return initialState;
    },
    setUser: (state, action) => {
      state.currentUser = action.payload;
    },
    resetState: (state) => {
      state.currentUser = {};
      state.isAuthenticated = false;
    },
    toggleSocketStatus(state, { payload }) {
      state.isSocketConnected = payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(LoginRequest.pending, () => {
      return initialState;
    });
    builder.addCase(LoginRequest.fulfilled, (state, { payload }) => {
      delete payload.vendor;
      setCookie(payload.expires_in);
      Cookies.remove('currentVendor');
      return {
        ...payload,
        role: payload.roles[0] || 'manager',
        isAuthenticated: true
      };
    });
    builder.addCase(LoginRequest.rejected, () => {
      return initialState;
    });
    builder.addCase(RefreshTokenRequest.pending, (state) => {
      return {
        isRefreshCalled: true
      };
    });
    builder.addCase(RefreshTokenRequest.fulfilled, (state, { payload }) => {
      setCookie(payload.expires_in);
      return {
        ...state,
        access_token: payload.access_token,
        expires_in: payload.expires_in,
        id_token: payload.id_token,
        token_type: payload.token_type,
        isRefreshCalled: false
      };
    });
    builder.addCase(RefreshTokenRequest.rejected, () => {
      return initialState;
    });
  }
});

function setCookie(expiresIn) {
  const expiryTime = new Date(new Date().getTime() + expiresIn * 1000);
  Cookies.set('expires_in', expiryTime, { expires: expiryTime });
}

export default AuthSlice.reducer;
export const AuthActions = AuthSlice.actions;
